// src/modules/CompetitionManager/components/RolePermissionsPanel.tsx
import React from 'react';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Permission } from '../../../utils/types';
import { RoleGroup } from '../index';

interface RolePermissionsPanelProps {
  role: string;
  roleGroup: RoleGroup;
  permissions: Permission[];
  setPermissions: React.Dispatch<React.SetStateAction<Permission[]>>;
  allPermissions: Permission[];
  onAddPermission: () => void;
  onSave: () => void;
  saving: boolean;
  loading: boolean;
}

const RolePermissionsPanel: React.FC<RolePermissionsPanelProps> = ({
  role,
  roleGroup,
  permissions,
  setPermissions,
  allPermissions,
  onAddPermission,
  onSave,
  saving,
  loading
}) => {
  
  /**
   * Helper function to get status styling based on Monday.com status values
   * Handles all possible Monday.com status values and provides appropriate styling
   */
  const getStatusStyle = (status: string | undefined | null): string => {
    if (!status) return 'bg-gray-100 text-gray-800';
    
    const statusLower = status.toLowerCase().trim();
    
    // Map all possible Monday.com statuses to appropriate colors
    // Handles variations in casing and spacing
    if (statusLower === 'done' || statusLower === 'active' || statusLower === 'complete' || statusLower === 'completed') {
      return 'bg-green-100 text-green-800';
    } else if (statusLower === 'in progress' || statusLower === 'working on it' || statusLower === 'in-progress') {
      return 'bg-blue-100 text-blue-800';
    } else if (statusLower === 'blocked' || statusLower === 'stuck') {
      return 'bg-red-100 text-red-800';
    } else if (statusLower === 'to do' || statusLower === 'to-do' || statusLower === 'todo' || statusLower === 'not started') {
      return 'bg-yellow-100 text-yellow-800';
    } else if (statusLower === 'under review' || statusLower === 'under-review' || statusLower === 'reviewing') {
      return 'bg-purple-100 text-purple-800';
    } else if (statusLower === 'cancelled' || statusLower === 'canceled') {
      return 'bg-gray-100 text-gray-800';
    } else if (statusLower === 'pending') {
      return 'bg-orange-100 text-orange-800';
    } else {
      // Default fallback for any unknown status
      return 'bg-gray-100 text-gray-800';
    }
  };

  /**
   * Helper function to display status with appropriate icon
   * Shows the actual Monday.com status text with optional icons
   */
  const getStatusDisplay = (status: string | undefined | null): string => {
    if (!status) return 'Pending';
    
    const statusLower = status.toLowerCase().trim();
    
    // Add checkmark for completed statuses
    if (statusLower === 'done' || statusLower === 'active' || statusLower === 'complete' || statusLower === 'completed') {
      return `✓ ${status}`;
    }
    // Add clock icon for in-progress statuses
    else if (statusLower === 'in progress' || statusLower === 'working on it') {
      return `🔄 ${status}`;
    }
    // Add warning icon for blocked statuses
    else if (statusLower === 'blocked' || statusLower === 'stuck') {
      return `⚠️ ${status}`;
    }
    // Add hourglass for pending/todo statuses
    else if (statusLower === 'to do' || statusLower === 'to-do' || statusLower === 'not started') {
      return `⏳ ${status}`;
    }
    // Return status as-is for all other cases
    else {
      return status;
    }
  };

  /**
   * Normalize status when setting it programmatically
   * This ensures consistency when we update status in the frontend
   */
  const normalizeStatusForUpdate = (status: string): string => {
    // When we set status programmatically (like when editing), use consistent values
    // This maintains backward compatibility with existing code
    if (status === 'to-do' || status === 'todo') {
      return 'To Do';  // Use Monday.com's expected format
    }
    if (status === 'active') {
      return 'Done';  // Map active to Done for Monday.com
    }
    return status;
  };

  const handleEdit = (permissionIndex: number) => {
    const globalIndex = allPermissions.findIndex(p => 
      p === permissions[permissionIndex]
    );
    
    if (globalIndex !== -1) {
      const updated = [...allPermissions];
      updated[globalIndex] = { ...updated[globalIndex], isEditing: true };
      setPermissions(updated);
    }
  };

  const handleCancelEdit = (permissionIndex: number) => {
    const perm = permissions[permissionIndex];
    const globalIndex = allPermissions.findIndex(p => p === perm);
    
    if (globalIndex !== -1) {
      const updated = [...allPermissions];
      if (updated[globalIndex].isNew && updated[globalIndex].name === '') {
        updated.splice(globalIndex, 1);
      } else {
        updated[globalIndex] = { ...updated[globalIndex], isEditing: false };
      }
      setPermissions(updated);
    }
  };

  const handleInputChange = (permissionIndex: number, field: keyof Permission, value: string) => {
    const globalIndex = allPermissions.findIndex(p => 
      p === permissions[permissionIndex]
    );
    
    if (globalIndex !== -1) {
      const updated = [...allPermissions];
      updated[globalIndex] = { ...updated[globalIndex], [field]: value };
      
      // When email changes on existing items, set status to "To Do" (normalized)
      if ((field === 'email') && !updated[globalIndex].isNew) {
        updated[globalIndex].status = normalizeStatusForUpdate('to-do');
      }
      
      setPermissions(updated);
    }
  };

  const handleDelete = (permissionIndex: number) => {
    const globalIndex = allPermissions.findIndex(p => 
      p === permissions[permissionIndex]
    );
    
    if (globalIndex !== -1) {
      const updated = [...allPermissions];
      updated.splice(globalIndex, 1);
      setPermissions(updated);
    }
  };

  const colorClasses = {
    purple: 'from-purple-50 to-purple-100 text-purple-700',
    blue: 'from-blue-50 to-blue-100 text-blue-700',
    green: 'from-green-50 to-green-100 text-green-700',
    orange: 'from-orange-50 to-orange-100 text-orange-700',
    gray: 'from-gray-50 to-gray-100 text-gray-700'
  }[roleGroup.color] || 'from-gray-50 to-gray-100 text-gray-700';

  const buttonColorClasses = {
    purple: 'from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800',
    blue: 'from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800',
    green: 'from-green-600 to-green-700 hover:from-green-700 hover:to-green-800',
    orange: 'from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800',
    gray: 'from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800'
  }[roleGroup.color] || 'from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Header */}
      <div className={`bg-gradient-to-r ${colorClasses} px-6 py-4 border-b border-gray-200`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/80 rounded-lg">
              {roleGroup.icon}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {role}
              </h3>
              <p className="text-sm text-gray-600">{roleGroup.description}</p>
            </div>
          </div>
          <button
            onClick={onAddPermission}
            className={`inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r ${buttonColorClasses} shadow-sm transition-all`}
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add User
          </button>
        </div>
      </div>

      {/* Permissions List */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : permissions.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            <p className="text-gray-500 font-medium">No users assigned to this role</p>
            <p className="text-gray-400 text-sm mt-1">Click "Add User" to assign permissions</p>
          </div>
        ) : (
          <div className="space-y-3">
            {permissions.map((permission, index) => (
              <div 
                key={index} 
                className={`border rounded-lg p-4 ${
                  permission.isNew ? 'border-blue-300 bg-blue-50/50' : 'border-gray-200'
                } ${permission.isEditing ? 'ring-2 ring-blue-500' : ''}`}
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                    {permission.isEditing || permission.isNew ? (
                      <input
                        type="text"
                        value={permission.name}
                        onChange={(e) => handleInputChange(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter name"
                      />
                    ) : (
                      <p className="text-sm font-medium text-gray-900 py-2">{permission.name || '-'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                    {permission.isEditing || permission.isNew ? (
                      <input
                        type="email"
                        value={permission.email}
                        onChange={(e) => handleInputChange(index, 'email', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter email"
                      />
                    ) : (
                      <p className="text-sm text-gray-700 py-2">{permission.email || '-'}</p>
                    )}
                  </div>

                  <div className="flex items-end justify-between">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusStyle(permission.status)}`}>
                        {getStatusDisplay(permission.status)}
                      </span>
                    </div>
                    
                    <div className="flex space-x-1 ml-3">
                      {permission.isEditing || permission.isNew ? (
                        <button
                          onClick={() => handleCancelEdit(index)}
                          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEdit(index)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                            title="Edit"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(index)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Save Button */}
        {permissions.length > 0 && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={onSave}
              disabled={saving}
              className={`
                inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-lg shadow-sm
                ${saving
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
                }
              `}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Changes
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RolePermissionsPanel;